/**
 * Theme helpers. The app reads and writes the color mode through
 * `@ordo/ui/use-dark-mode` directly; this file adds the two things that
 * hook can't own:
 *  - `useTheme()` — the `isDark` boolean shape the review layer expects.
 *  - `applyDefaultThemePreference()` — seeding from the multipass account.
 *
 * The storage key is 'theme', matching the hook's default and the
 * pre-paint bootstrap in `client/src/index.html`.
 */

import { type ColorMode, isColorMode, resolveColorMode, useDarkMode } from '@ordo/ui/use-dark-mode'

const STORAGE_KEY = 'theme'

export function useTheme(): { isDark: boolean } {
  const { isDark } = useDarkMode()
  return { isDark }
}

/**
 * Apply the account-level default (`getColorMode(user)` from multipass)
 * when this device has no local override yet. A local pick always wins:
 * once the user touches the in-app control the value lands in
 * localStorage and this becomes a no-op. Deliberately does NOT write to
 * storage, so the account value keeps applying until that happens.
 * Idempotent — safe to call on every auth refresh.
 */
export function applyDefaultThemePreference(preference: ColorMode | null | undefined): void {
  if (!preference) {
    return
  }
  try {
    if (isColorMode(window.localStorage?.getItem(STORAGE_KEY))) {
      return
    }
  } catch {
    // Storage blocked (private mode) — there is no local override to respect.
  }
  const root = document.documentElement
  root.classList.remove('light', 'dark')
  root.classList.add(resolveColorMode(preference))
}
